import { useEffect } from 'react';

/**
 * Central shortcut registry. `mod` = Ctrl on Windows/Linux, Cmd on macOS.
 * One list so the settings dialog can show them and conflicts are visible in one place.
 */
export interface Shortcut {
  id:
    | 'palette'
    | 'quickSwitcher'
    | 'newPage'
    | 'dailyNote'
    | 'search'
    | 'capture'
    | 'tasks'
    | 'closeTab'
    | 'toggleSidebar'
    | 'settings'
    | 'ai';
  keys: string;
  run: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function formatKeys(keys: string): string {
  return keys
    .split('+')
    .map((k) =>
      k === 'mod'
        ? isMac
          ? '⌘'
          : 'Ctrl'
        : k === 'shift'
          ? isMac
            ? '⇧'
            : 'Shift'
          : k === 'enter'
            ? 'Enter'
            : k === 'space'
              ? 'Space'
              : k.toUpperCase(),
    )
    .join(isMac ? '' : '+');
}

function matches(e: KeyboardEvent, keys: string): boolean {
  const parts = keys.split('+');
  const key = parts[parts.length - 1]!;
  const mod = isMac ? e.metaKey : e.ctrlKey;
  return (
    parts.includes('mod') === mod &&
    parts.includes('shift') === e.shiftKey &&
    parts.includes('alt') === e.altKey &&
    (key === 'space'
      ? e.code === 'Space'
      : // Alt/Option changes e.key on some layouts; fall back to the physical key.
        e.key.toLowerCase() === key ||
        (/^[a-z]$/.test(key) && e.code === `Key${key.toUpperCase()}`))
  );
}

export function useShortcuts(shortcuts: Shortcut[]): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      for (const s of shortcuts) {
        if (matches(e, s.keys)) {
          e.preventDefault();
          s.run();
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts]);
}

export const SHORTCUT_KEYS: Record<Shortcut['id'], string> = {
  palette: 'mod+k',
  quickSwitcher: 'mod+o',
  newPage: 'mod+alt+n',
  dailyNote: 'mod+shift+d',
  search: 'mod+shift+f',
  capture: 'mod+shift+space',
  tasks: 'mod+alt+t',
  closeTab: 'mod+alt+w',
  toggleSidebar: 'mod+\\',
  settings: 'mod+,',
  ai: 'mod+j',
};
